/**
 * Needs and their quotes: what stage a need is at, how its quotes compare, and
 * what a deposit comes to. The server answers the Plan view with these and the
 * comparison draws its matrix from them, so the two can never disagree.
 */
import { ExpensePhase } from '../types/expense.js';
import { QuoteStatus } from '../types/quote.js';
import { isIsoDay, officeCalendarDate } from './checklist.js';
import { isPositiveAmount, multiplyMoney, roundMoney, sumMoney, toCents } from './money.js';
/** A quote still in play: asked, or back and not yet decided. */
export function isOpenQuote(status) {
    return status === QuoteStatus.REQUESTED || status === QuoteStatus.RECEIVED;
}
/**
 * The need's stage, from what it holds. `balance` is what is still owed the
 * vendor once booked; a booked line with nothing owed is paid in full — but a
 * $0 line nobody has paid is only booked (a comped vendor is not "paid").
 */
export function deriveNeedStage(input) {
    if (input.phase === ExpensePhase.BOOKED) {
        const price = input.price ?? 0;
        const balance = input.balance ?? price;
        return isPositiveAmount(price) && !isPositiveAmount(balance) ? 'PAID' : 'BOOKED';
    }
    const live = input.quotes.filter((q) => q.status !== QuoteStatus.WITHDRAWN);
    if (live.some((q) => q.status !== QuoteStatus.REQUESTED && q.totalAmount !== null)) {
        return 'SOURCING';
    }
    if (live.some((q) => isOpenQuote(q.status)))
        return 'REQUESTED';
    return 'NEW';
}
/** The Plan view's one-line account of a need's quotes. */
export function summarizeQuotes(quotes) {
    const inPlay = quotes.filter((q) => isOpenQuote(q.status) || q.status === QuoteStatus.ACCEPTED);
    const back = inPlay.filter((q) => q.status !== QuoteStatus.REQUESTED && q.totalAmount !== null);
    let lowest = null;
    let highest = null;
    for (const q of back) {
        if (!lowest || toCents(q.totalAmount) < toCents(lowest.totalAmount))
            lowest = q;
        if (!highest || toCents(q.totalAmount) > toCents(highest.totalAmount))
            highest = q;
    }
    const expiries = inPlay
        .filter((q) => isOpenQuote(q.status) && q.validUntil)
        .map((q) => q.validUntil)
        .sort();
    const requested = quotes
        .map((q) => q.requestedAt)
        .filter((d) => !!d)
        .sort();
    return {
        asked: inPlay.length,
        back: back.length,
        lowest: lowest ? roundMoney(lowest.totalAmount) : null,
        highest: highest ? roundMoney(highest.totalAmount) : null,
        lowestVendorName: lowest?.vendorName ?? null,
        soonestExpiry: expiries[0] ?? null,
        lastRequestedAt: requested.length ? requested[requested.length - 1] : null,
        filesWaiting: inPlay.filter((q) => q.fileReceivedAt && q.status === QuoteStatus.REQUESTED && q.totalAmount === null).length,
    };
}
/** What a deposit comes to, in dollars, against a total; null when it cannot be known. */
export function depositAmount(terms, total) {
    if (!terms)
        return null;
    if (terms.amount !== undefined)
        return roundMoney(terms.amount);
    if (terms.percent !== undefined && total !== null) {
        return Math.round((toCents(total) * terms.percent) / 100) / 100;
    }
    return null;
}
/**
 * The deposit's due day once a quote is accepted: "signing" is the office day
 * of the accept (accepting is the agreement), a written day stays as it is.
 */
export function resolveDepositDueDate(terms, acceptedAt) {
    if (!terms)
        return null;
    if (terms.due === 'signing')
        return officeCalendarDate(acceptedAt).toISOString().slice(0, 10);
    return isIsoDay(terms.due) ? terms.due : null;
}
/** "30% on signing", "$1,000 by 2026-10-01". */
export function describeDepositTerms(terms, formatAmount) {
    if (!terms)
        return '';
    const size = terms.percent !== undefined ? `${terms.percent}%` : formatAmount(terms.amount ?? 0);
    const when = terms.dueDate ?? terms.due;
    return when === 'signing' ? `${size} on signing` : `${size} by ${when}`;
}
/**
 * The comparison of a need's quotes: one row per item, then the extras a
 * vendor added (lines that price no item), so nothing quoted is hidden. The
 * lowest amount in each row is marked when more than one vendor priced it.
 */
export function buildQuoteComparison(items, quotes, budget) {
    const rows = items.map((item) => ({
        key: item.id,
        label: item.itemName || 'Untitled item',
        quantity: item.quantity,
        extra: false,
        cells: Object.fromEntries(quotes.map((q) => {
            const lines = q.lineItems.filter((l) => l.planningItemId === item.id);
            return [
                q.id,
                { amount: lines.length ? sumMoney(lines.map((l) => l.totalPrice)) : null, lowest: false },
            ];
        })),
    }));
    const known = new Set(items.map((i) => i.id));
    for (const q of quotes) {
        q.lineItems.forEach((line, index) => {
            if (line.planningItemId && known.has(line.planningItemId))
                return;
            rows.push({
                key: `${q.id}:${index}`,
                label: line.description,
                quantity: line.quantity,
                extra: true,
                cells: Object.fromEntries(quotes.map((other) => [
                    other.id,
                    { amount: other.id === q.id ? roundMoney(line.totalPrice) : null, lowest: false },
                ])),
            });
        });
    }
    for (const row of rows)
        markLowest(row.cells);
    const totals = Object.fromEntries(quotes.map((q) => [
        q.id,
        { amount: q.totalAmount === null ? null : roundMoney(q.totalAmount), lowest: false },
    ]));
    markLowest(totals);
    const vsBudget = Object.fromEntries(quotes.map((q) => [
        q.id,
        q.totalAmount === null || budget === null ? null : roundMoney(q.totalAmount - budget),
    ]));
    const lowestQuoteId = Object.entries(totals).find(([, c]) => c.lowest)?.[0] ?? null;
    return { rows, totals, vsBudget, lowestQuoteId };
}
function markLowest(cells) {
    const priced = Object.values(cells).filter((c) => c.amount !== null);
    if (priced.length < 2)
        return;
    const min = Math.min(...priced.map((c) => toCents(c.amount)));
    for (const c of priced)
        c.lowest = toCents(c.amount) === min;
}
/** An item line's estimate: quantity × unit price, in cents. */
export function itemsEstimate(items) {
    return sumMoney(items.map((i) => multiplyMoney(i.quantity, i.unitPrice)));
}
//# sourceMappingURL=needs.js.map